/**
 * API Key 관리 컴포넌트
 */

import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Badge, Button, Modal, Alert, Form, Table, Row, Col } from 'react-bootstrap';
import { partnerService } from '../../services/partnerService';
import { ApiKeyResponse, Partner } from '../../types/partner';
import { format } from 'date-fns';

interface ApiKeyManagerProps {
  partnerId: number;
  partner: Partner;
}

const ApiKeyManager: React.FC<ApiKeyManagerProps> = ({
  partnerId,
  partner,
}) => {
  const hasApiKey = !!partner.api_key;
  const maskedApiKey = partner.api_key;
  const queryClient = useQueryClient();
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [apiKey, setApiKey] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  // API Key 이력 조회
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['partnerApiKeyHistory', partnerId],
    queryFn: () => partnerService.getApiKeyHistory(partnerId, { limit: 20 }),
    enabled: showHistory,
  });

  const generateMutation = useMutation({
    mutationFn: () => partnerService.generateApiKey(partnerId),
    onSuccess: (data: ApiKeyResponse) => {
      setApiKey(data.api_key);
      setShowGenerateModal(false);
      setShowApiKeyModal(true);
      queryClient.invalidateQueries({ queryKey: ['partner', partnerId] });
    },
    onError: (error: any) => {
      alert(`API Key 생성 실패: ${error.message || '알 수 없는 오류가 발생했습니다.'}`);
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: () => partnerService.regenerateApiKey(partnerId),
    onSuccess: (data: ApiKeyResponse) => {
      setApiKey(data.api_key);
      setShowRegenerateModal(false);
      setShowApiKeyModal(true);
      queryClient.invalidateQueries({ queryKey: ['partner', partnerId] });
    },
    onError: (error: any) => {
      alert(`API Key 재생성 실패: ${error.message || '알 수 없는 오류가 발생했습니다.'}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => partnerService.deleteApiKey(partnerId),
    onSuccess: () => {
      setShowDeleteModal(false);
      queryClient.invalidateQueries({ queryKey: ['partner', partnerId] });
      alert('API Key가 삭제되었습니다.');
    },
    onError: (error: any) => {
      alert(`API Key 삭제 실패: ${error.message || '알 수 없는 오류가 발생했습니다.'}`);
    },
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      alert('복사에 실패했습니다.');
    }
  };

  const handleGenerate = () => {
    if (hasApiKey) {
      setShowRegenerateModal(true);
    } else {
      setShowGenerateModal(true);
    }
  };

  const handleCloseApiKeyModal = () => {
    setShowApiKeyModal(false);
    setApiKey('');
    setCopied(false);
  };

  return (
    <>
      <Card>
        <Card.Header>
          <h5 className="mb-0">API Key 관리</h5>
        </Card.Header>
        <Card.Body>
          <div className="mb-3">
            <label className="text-muted small">API Key 상태</label>
            <div>
              {hasApiKey ? (
                <div>
                  <Badge bg="success" className="me-2">활성</Badge>
                  <span style={{ color: '#ccc', fontFamily: 'monospace' }}>
                    {maskedApiKey || 'pk_***'}
                  </span>
                </div>
              ) : (
                <Badge bg="secondary">없음</Badge>
              )}
            </div>
          </div>

          {/* API Key 통계 */}
          {hasApiKey && (
            <Row className="mb-3">
              <Col md={6}>
                <div className="mb-2">
                  <label className="text-muted small">생성일시</label>
                  <div className="small">
                    {partner.api_key_created_at
                      ? format(new Date(partner.api_key_created_at), 'yyyy-MM-dd HH:mm:ss')
                      : '-'}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-2">
                  <label className="text-muted small">마지막 사용일시</label>
                  <div className="small">
                    {partner.api_key_last_used_at
                      ? format(new Date(partner.api_key_last_used_at), 'yyyy-MM-dd HH:mm:ss')
                      : '사용 이력 없음'}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-2">
                  <label className="text-muted small">사용 횟수</label>
                  <div className="small fw-bold" style={{ color: '#00BCD4' }}>
                    {partner.api_key_usage_count?.toLocaleString() || 0}회
                  </div>
                </div>
              </Col>
            </Row>
          )}

          <Alert variant="warning" className="mb-3">
            <small>
              ⚠️ <strong>중요:</strong> API Key는 보안상 마스킹되어 표시됩니다.
              <br />
              • 생성/재생성 시에만 평문으로 확인할 수 있습니다.
              <br />
              • 재생성 시 기존 API Key는 더 이상 사용할 수 없습니다.
              <br />
              • API Key는 안전하게 보관하고 로그에 기록하지 마세요.
            </small>
          </Alert>

          <div className="d-flex gap-2">
            <Button
              variant="primary"
              onClick={handleGenerate}
              disabled={generateMutation.isPending || regenerateMutation.isPending}
            >
              {hasApiKey ? 'API Key 재생성' : 'API Key 생성'}
            </Button>
            {hasApiKey && (
              <>
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowHistory(!showHistory)}
                >
                  {showHistory ? '이력 숨기기' : '이력 보기'}
                </Button>
                <Button
                  variant="danger"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={deleteMutation.isPending}
                >
                  API Key 삭제
                </Button>
              </>
            )}
          </div>

          {/* API Key 이력 */}
          {showHistory && hasApiKey && (
            <div className="mt-4">
              <h6 className="mb-3">API Key 이력</h6>
              {historyLoading ? (
                <div className="text-center py-3">
                  <small className="text-muted">로딩 중...</small>
                </div>
              ) : historyData && historyData.history.length > 0 ? (
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>작업 유형</th>
                      <th>작업자</th>
                      <th>작업 일시</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.history.map((item) => (
                      <tr key={item.id}>
                        <td>
                          {item.action === 'generate' && (
                            <Badge bg="success">생성</Badge>
                          )}
                          {item.action === 'regenerate' && (
                            <Badge bg="warning" text="dark">재생성</Badge>
                          )}
                          {item.action === 'delete' && (
                            <Badge bg="danger">삭제</Badge>
                          )}
                        </td>
                        <td>
                          {item.created_by_name || item.created_by_username || '-'}
                        </td>
                        <td>
                          {format(new Date(item.created_at), 'yyyy-MM-dd HH:mm:ss')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center py-3">
                  <small className="text-muted">이력이 없습니다.</small>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* 생성 확인 모달 */}
      <Modal show={showGenerateModal} onHide={() => setShowGenerateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>API Key 생성</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>API Key를 생성하시겠습니까?</p>
          <Alert variant="info" className="mb-0">
            <small>
              생성된 API Key는 한 번만 표시됩니다. 안전하게 보관하세요.
            </small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGenerateModal(false)}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
          >
            {generateMutation.isPending ? '생성 중...' : '생성'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 재생성 확인 모달 */}
      <Modal show={showRegenerateModal} onHide={() => setShowRegenerateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>API Key 재생성</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-3">
            <strong>경고:</strong> 기존 API Key는 더 이상 사용할 수 없게 됩니다.
            <br />
            제휴사에 사전 통보 후 진행하시기 바랍니다.
          </Alert>
          <p>정말 API Key를 재생성하시겠습니까?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRegenerateModal(false)}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={() => regenerateMutation.mutate()}
            disabled={regenerateMutation.isPending}
          >
            {regenerateMutation.isPending ? '재생성 중...' : '재생성'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 삭제 확인 모달 */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>API Key 삭제</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger" className="mb-3">
            <strong>경고:</strong> API Key를 삭제하면 더 이상 사용할 수 없게 됩니다.
            <br />
            제휴사에 사전 통보 후 진행하시기 바랍니다.
          </Alert>
          <p>정말 API Key를 삭제하시겠습니까?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            취소
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? '삭제 중...' : '삭제'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* API Key 표시 모달 */}
      <Modal show={showApiKeyModal} onHide={handleCloseApiKeyModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>API Key</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="success" className="mb-3">
            <strong>성공:</strong> API Key가 생성되었습니다.
            <br />
            <small>이 화면을 닫으면 다시 확인할 수 없습니다. 반드시 복사하여 안전하게 보관하세요.</small>
          </Alert>
          <Form.Group className="mb-3">
            <Form.Label>API Key</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                value={apiKey}
                readOnly
                style={{ fontFamily: 'monospace' }}
              />
              <Button
                variant="outline-primary"
                onClick={handleCopy}
                disabled={copied}
              >
                {copied ? '복사됨!' : '복사'}
              </Button>
            </div>
          </Form.Group>
          <Alert variant="info" className="mb-0">
            <small>
              <strong>보안 안내:</strong>
              <br />
              • API Key를 환경 변수로 관리하세요.
              <br />
              • Git 저장소에 커밋하지 마세요.
              <br />
              • 로그에 기록하지 마세요.
              <br />
              • 제휴사 보안 요구사항 문서를 참조하세요.
            </small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseApiKeyModal}>
            확인
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ApiKeyManager;

